/**
 * Timeline View Component
 * Handles all timeline-specific rendering and logic
 */

import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TimelineProjectBar } from './TimelineProjectBar';
import { TimelineMarkers } from './TimelineMarkers';
import { UnifiedProject, TimelinePosition, TimeScale } from './types';
import { 
  getTimelineBounds, 
  generateTimelineMarkers, 
  generateActivityMarkers, 
  assignLanes 
} from './timelineUtils';

const TIMELINE_HEIGHT = 80;
const TIMELINE_PADDING = 20;

interface TimelineViewProps {
  sortedProjects: UnifiedProject[];
  timeScale: TimeScale;
  zoomLevel: number;
  containerWidth: number;
  showCompanyLogos: boolean;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  sortedProjects,
  timeScale,
  zoomLevel,
  containerWidth,
  showCompanyLogos
}) => {
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });

  // Mouse drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!timelineContainerRef.current) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.pageX,
      scrollLeft: timelineContainerRef.current.scrollLeft,
    });
    // Add global mouse listeners for dragging
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !timelineContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX;
    const walk = (x - dragStart.x) * 1; // Scroll speed multiplier
    timelineContainerRef.current.scrollLeft = dragStart.scrollLeft - walk;
  }, [isDragging, dragStart]);

  const handleGlobalMouseUp = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleGlobalMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // This is now handled by global listeners
  }, []);

  const handleMouseUp = useCallback(() => {
    // This is now handled by global listeners
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Keep dragging even when mouse leaves the container
  }, []);

  // Touch drag handlers for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!timelineContainerRef.current) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.pageX,
      scrollLeft: timelineContainerRef.current.scrollLeft,
    });
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !timelineContainerRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const x = touch.pageX;
    const walk = (x - dragStart.x) * 1; // Scroll speed multiplier
    timelineContainerRef.current.scrollLeft = dragStart.scrollLeft - walk;
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Cleanup effect for global listeners
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [handleGlobalMouseMove, handleGlobalMouseUp]);

  // Calculate timeline positions for timeline view
  const timelinePositions = useMemo((): TimelinePosition[] => {
    if (!sortedProjects.length) return [];

    const bounds = getTimelineBounds(sortedProjects, timeScale);
    // For timeline view, ensure we use a minimum width that provides good spacing
    const baseWidth = Math.max(containerWidth, 1200); // Minimum 1200px for good timeline spacing
    const effectiveWidth = baseWidth * zoomLevel; // Remove Math.max to allow zooming out below 100%
    const margin = 40;
    const availableWidth = effectiveWidth - (2 * margin);

    return sortedProjects.map(project => {
      const startTime = new Date(project.startDate || '1970-01-01').getTime();
      const endTime = project.endDate && project.endDate !== 'Present' 
        ? new Date(project.endDate).getTime() 
        : Date.now();
      
      const duration = endTime - startTime;
      const startOffset = (startTime - bounds.startDate.getTime()) / bounds.totalDuration;
      const width = Math.max(duration / bounds.totalDuration, 0.02); // Minimum 2% width

      return {
        left: margin + (startOffset * availableWidth),
        width: width * availableWidth
      };
    });
  }, [sortedProjects, timeScale, zoomLevel, containerWidth]);

  // Calculate timeline markers
  const timelineMarkers = useMemo(() => {
    if (!sortedProjects.length) return [];
    const bounds = getTimelineBounds(sortedProjects, timeScale);
    return generateTimelineMarkers(bounds, containerWidth, zoomLevel, timeScale);
  }, [sortedProjects, timeScale, zoomLevel, containerWidth]);

  // Calculate activity markers
  const activityMarkers = useMemo(() => {
    if (!sortedProjects.length) return [];
    const bounds = getTimelineBounds(sortedProjects, timeScale);
    return generateActivityMarkers(bounds, sortedProjects, containerWidth, zoomLevel);
  }, [sortedProjects, timeScale, zoomLevel, containerWidth]);

  // Calculate lane assignments for timeline view
  const projectLanes = useMemo((): number[] => {
    if (!sortedProjects.length) return [];
    return assignLanes(sortedProjects);
  }, [sortedProjects]);

  return (
    <motion.div
      key="timeline"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      ref={timelineContainerRef}
      className="relative overflow-x-auto w-screen -mx-6 px-6 select-none"
      style={{ 
        height: `${(Math.max(...projectLanes, 0) + 1) * TIMELINE_HEIGHT + TIMELINE_PADDING + 100}px`, 
        minHeight: '220px',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Timeline markers (background, lines, headers, logos) */}
      <TimelineMarkers
        timelineMarkers={timelineMarkers}
        activityMarkers={activityMarkers}
        containerWidth={containerWidth}
        zoomLevel={zoomLevel}
        projectLanes={projectLanes}
        TIMELINE_HEIGHT={TIMELINE_HEIGHT}
        TIMELINE_PADDING={TIMELINE_PADDING}
      />
      
      {/* Projects in timeline */}
      <div 
        className="pt-16 relative"
        style={{ 
          width: `${Math.max(containerWidth, 1200) * zoomLevel}px`,
          zIndex: 30,
          isolation: 'isolate',
          position: 'relative'
        }}
      >
        {sortedProjects.map((project, index) => {
          const lane = projectLanes[index] || 0;
          // Add extra vertical offset to push the first project (and all projects) lower
          const yPosition = TIMELINE_PADDING + (lane * TIMELINE_HEIGHT) + 60;
          
          return (
            <TimelineProjectBar
              key={project.id}
              project={project}
              position={timelinePositions[index]}
              yPosition={yPosition}
              showCompanyLogos={showCompanyLogos}
              index={index}
            />
          );
        })}
      </div>

      {/* Timeline scale indicator */}
      <div 
        className="absolute bottom-2 left-4 flex justify-between text-xs text-muted-foreground"
        style={{ 
          width: `${Math.max(containerWidth, 1200) * zoomLevel - 32}px`,
          zIndex: 40
        }}
      >
        <span>Scale: {timeScale === 'month' ? 'Months' : timeScale === 'quarter' ? 'Quarters' : 'Years'}</span>
        <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
      </div>
    </motion.div>
  );
};